<template>
  <div class="theme-switcher">
    <div v-if="showOptions" class="theme-options">
      <div class="theme-option-header">
        <span>主题设置</span>
        <button class="close-btn" @click="showOptions = false">✕</button>
      </div>
      
      <div class="theme-mode-toggle">
        <span>夜间模式</span>
        <div 
          class="toggle-switch"
          :class="{ 'active': themeStore.isDarkMode }"
          @click="themeStore.toggleDarkMode()"
        >
          <div class="toggle-slider"></div>
        </div>
      </div>
      
      <div class="theme-colors">
        <div class="theme-color-title">主题颜色</div>
        <div class="theme-color-options">
          <div 
            class="theme-color-option" 
            :class="{ 'active': themeStore.currentTheme === themeStore.themes.light && !themeStore.isDarkMode }"
            @click="themeStore.setTheme(themeStore.themes.light)"
          >
            <div class="color-preview light-theme"></div>
            <span>默认</span>
          </div>
          <div 
            class="theme-color-option"
            :class="{ 'active': themeStore.currentTheme === themeStore.themes.green }"
            @click="themeStore.setTheme(themeStore.themes.green)"
          >
            <div class="color-preview green-theme"></div>
            <span>绿色</span>
          </div>
          <div 
            class="theme-color-option"
            :class="{ 'active': themeStore.currentTheme === themeStore.themes.blue }"
            @click="themeStore.setTheme(themeStore.themes.blue)"
          >
            <div class="color-preview blue-theme"></div>
            <span>蓝色</span>
          </div>
        </div>
      </div>
      
      <div class="theme-system-follow">
        <button class="system-btn" @click="followSystem">跟随系统主题</button>
      </div>
    </div>
    
    <button class="theme-toggle-btn" @click="showOptions = !showOptions">
      <span v-if="themeStore.isDarkMode">🌙</span>
      <span v-else>☀️</span>
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useThemeStore } from '@/stores/themeStore'

const themeStore = useThemeStore()
const showOptions = ref(false)

const followSystem = () => {
  themeStore.followSystemTheme()
  showOptions.value = false
}

onMounted(() => {
  themeStore.initTheme()
})
</script>

<style lang="scss" scoped>
.theme-switcher {
  position: relative;
}

.theme-toggle-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--secondary-bg-color);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
}

.theme-options {
  position: absolute;
  top: 50px;
  right: 0;
  width: 280px;
  background-color: var(--secondary-bg-color);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
  color: var(--text-color);
}

.theme-option-header {
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--secondary-bg-color);
  
  span {
    font-weight: bold;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    color: var(--secondary-text-color);
    
    &:hover {
      color: var(--text-color);
    }
  }
}

.theme-mode-toggle {
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  
  .toggle-switch {
    width: 46px;
    height: 24px;
    background-color: #dcdfe6;
    border-radius: 12px;
    position: relative;
    cursor: pointer;
    transition: background-color 0.3s;
    
    &.active {
      background-color: var(--primary-color);
    }
    
    .toggle-slider {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      background-color: #fff;
      border-radius: 50%;
      transition: left 0.3s;
    }
    
    &.active .toggle-slider {
      left: 24px;
    }
  }
}

.theme-colors {
  padding: 15px;
  border-bottom: 1px solid var(--border-color);
  
  .theme-color-title {
    margin-bottom: 12px;
  }
  
  .theme-color-options {
    display: flex;
    gap: 10px;
    
    .theme-color-option {
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      
      .color-preview {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        margin-bottom: 5px;
        border: 2px solid transparent;
        transition: all 0.3s;
        
        &.light-theme {
          background-color: #f5f7fa;
          border: 1px solid #dcdfe6;
        }
        
        &.green-theme {
          background-color: #0e6736;
        }
        
        &.blue-theme {
          background-color: #3e7bfa;
        }
      }
      
      &.active .color-preview {
        border: 2px solid var(--primary-color);
        transform: scale(1.1);
      }
      
      span {
        font-size: 12px;
      }
    }
  }
}

.theme-system-follow {
  padding: 15px;
  text-align: center;
  
  .system-btn {
    padding: 8px 16px;
    background-color: var(--primary-color);
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      background-color: var(--primary-hover-color);
    }
  }
}
</style> 